import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useI18n } from "@/i18n";
import { Menu, X } from "lucide-react";

const NAV_LINKS = [
  { id: "services", key: "services" },
  { id: "portfolio", key: "portfolio" },
  { id: "process", key: "process" },
  { id: "why", key: "about" },
  { id: "quote", key: "contact" },
];

export default function Header() {
  const { lang, setLang, t } = useI18n();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    setOpen(false);
  };

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-black/60 border-b border-white/5"
          : "bg-transparent"
      }`}
    >
      <div className="container mx-auto px-6 md:px-12 flex items-center justify-between h-16 md:h-20">
        <button
          data-testid="logo-button"
          onClick={() => go("hero")}
          className="font-display text-lg md:text-xl tracking-[0.18em] uppercase font-light hover:opacity-70 transition-opacity"
        >
          Sue<span className="opacity-50"> · </span>
          <span className="font-semibold">Production</span>
        </button>

        <nav className="hidden md:flex items-center gap-10">
          {NAV_LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}`}
              onClick={() => go(l.id)}
              className="text-xs uppercase tracking-[0.22em] text-white/60 hover:text-white transition-colors"
            >
              {t.nav[l.key]}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3 md:gap-5">
          <div
            data-testid="lang-switcher"
            className="hidden sm:flex items-center gap-1 text-xs uppercase tracking-[0.2em] luxury-glass rounded-full px-2 py-1"
          >
            <button
              data-testid="lang-en"
              onClick={() => setLang("en")}
              className={`px-2 py-1 rounded-full transition-colors ${
                lang === "en" ? "bg-white text-black" : "text-white/60 hover:text-white"
              }`}
            >
              EN
            </button>
            <button
              data-testid="lang-es"
              onClick={() => setLang("es")}
              className={`px-2 py-1 rounded-full transition-colors ${
                lang === "es" ? "bg-white text-black" : "text-white/60 hover:text-white"
              }`}
            >
              ES
            </button>
          </div>

          <button
            data-testid="header-cta"
            onClick={() => go("quote")}
            className="hidden md:inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] bg-white text-black px-5 py-2.5 rounded-full hover:bg-zinc-200 transition-colors"
          >
            {t.nav.cta}
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-black animate-soft-pulse" />
          </button>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((o) => !o)}
            aria-label="Menu"
            className="md:hidden p-2 text-white/80"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="md:hidden overflow-hidden bg-black border-t border-white/5"
            data-testid="mobile-menu"
          >
            <div className="container mx-auto px-6 py-6 flex flex-col gap-5">
              {NAV_LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`mobile-nav-${l.id}`}
                  onClick={() => go(l.id)}
                  className="text-left text-base uppercase tracking-[0.2em] text-white/80 hover:text-white"
                >
                  {t.nav[l.key]}
                </button>
              ))}
              <div className="flex items-center gap-2 pt-4 border-t border-white/10">
                <button
                  onClick={() => setLang("en")}
                  className={`px-3 py-1.5 text-xs rounded-full ${
                    lang === "en" ? "bg-white text-black" : "text-white/60"
                  }`}
                >
                  EN
                </button>
                <button
                  onClick={() => setLang("es")}
                  className={`px-3 py-1.5 text-xs rounded-full ${
                    lang === "es" ? "bg-white text-black" : "text-white/60"
                  }`}
                >
                  ES
                </button>
              </div>
              <button
                data-testid="mobile-header-cta"
                onClick={() => go("quote")}
                className="mt-2 bg-white text-black rounded-full py-3 text-sm uppercase tracking-[0.22em]"
              >
                {t.nav.cta}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
